import { useEffect, useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Upload, Trash2, Save, CheckCircle2, Link as LinkIcon, AlertCircle, Film, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absoluteUrl = (u) => (!u ? null : (u.startsWith('http') ? u : `${BACKEND_URL}${u}`));

const StoryEditor = ({ story, token, onSaved }) => {
  const [draft, setDraft] = useState(story);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileRef = useRef(null);

  useEffect(() => { setDraft(story); }, [story]);

  const updateField = (k, v) => setDraft((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        title: draft.title,
        tagline: draft.tagline,
        description: draft.description,
        duration_label: draft.duration_label,
        external_url: draft.external_url || null,
        poster_url: draft.poster_url || null,
        thumbnail_url: draft.thumbnail_url || null,
      };
      const res = await api.adminUpdateSuccessStory(token, story.slot, payload);
      toast.success('Saved');
      if (onSaved && res.story) onSaved(res.story);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      toast.error('File must be a video');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const res = await api.adminUploadSuccessVideo(token, story.slot, file, setUploadProgress);
      toast.success('Video uploaded');
      if (onSaved && res.story) onSaved(res.story);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleClearVideo = async () => {
    if (!confirm(`Clear the uploaded video for "${story.title}"?`)) return;
    try {
      await api.adminClearSuccessVideo(token, story.slot);
      toast.success('Video cleared');
      if (onSaved) onSaved({ ...story, video_url: null, video_filename: null, external_url: null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clear video');
    }
  };

  const hasUpload = !!draft.video_url;
  const posterPreview = absoluteUrl(draft.thumbnail_url || draft.poster_url);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={`admin-story-editor-${story.slot}`}>
      {/* Slot header */}
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
            Slot · {story.slot === 'hero' ? 'Featured Hero' : `Supporting ${story.position}`}
          </div>
          <div className="text-lg font-bold text-slate-900 font-['Outfit']">{draft.title || 'Untitled'}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {hasUpload ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full">
              <CheckCircle2 className="w-3 h-3" />
              MP4 uploaded
            </span>
          ) : draft.external_url ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 bg-blue-100 px-2 py-1 rounded-full">
              <LinkIcon className="w-3 h-3" />
              External URL
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-100 px-2 py-1 rounded-full">
              <AlertCircle className="w-3 h-3" />
              No video yet
            </span>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Left: text fields */}
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title</Label>
            <Input
              value={draft.title || ''}
              onChange={(e) => updateField('title', e.target.value)}
              data-testid={`story-title-${story.slot}`}
            />
          </div>
          <div>
            <Label className="text-xs">Tagline (ending line / hero quote)</Label>
            <Input
              value={draft.tagline || ''}
              onChange={(e) => updateField('tagline', e.target.value)}
              data-testid={`story-tagline-${story.slot}`}
            />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <textarea
              value={draft.description || ''}
              onChange={(e) => updateField('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid={`story-description-${story.slot}`}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Duration label</Label>
              <Input
                value={draft.duration_label || ''}
                onChange={(e) => updateField('duration_label', e.target.value)}
                placeholder="e.g., 45s"
                data-testid={`story-duration-${story.slot}`}
              />
            </div>
          </div>
        </div>

        {/* Right: media */}
        <div className="space-y-3">
          {/* MP4 Upload */}
          <div>
            <Label className="text-xs">MP4 video file</Label>
            <div className="flex items-center gap-2 mt-1">
              <input
                ref={fileRef}
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                onChange={(e) => handleUpload(e.target.files?.[0])}
                disabled={uploading}
                className="block w-full text-xs text-slate-600 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 disabled:opacity-50"
                data-testid={`story-upload-input-${story.slot}`}
              />
              {hasUpload && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleClearVideo}
                  className="text-rose-600 border-rose-200 hover:bg-rose-50 shrink-0"
                  data-testid={`story-clear-video-${story.slot}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
            {uploading && (
              <div className="mt-2">
                <div className="flex items-center gap-2 text-[11px] text-slate-600">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Uploading… {uploadProgress}%
                </div>
                <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden mt-1">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${uploadProgress}%` }} />
                </div>
              </div>
            )}
            {draft.video_filename && !uploading && (
              <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                <Film className="w-3 h-3" />
                {draft.video_filename}{draft.video_size_mb ? ` · ${draft.video_size_mb}MB` : ''}
              </div>
            )}
            <p className="text-[10px] text-slate-400 mt-1">MP4/WebM/MOV up to 50MB. Stored in MongoDB — survives redeploys.</p>
          </div>

          {/* YouTube/Vimeo URL */}
          <div>
            <Label className="text-xs">…or YouTube / Vimeo URL</Label>
            <Input
              value={draft.external_url || ''}
              onChange={(e) => updateField('external_url', e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              data-testid={`story-external-${story.slot}`}
              disabled={hasUpload}
            />
            <p className="text-[10px] text-slate-400 mt-1">
              {hasUpload ? 'Clear the MP4 to use an external URL instead.' : 'Used as a fallback when no MP4 is uploaded.'}
            </p>
          </div>

          {/* Poster / Thumbnail */}
          <div>
            <Label className="text-xs">Poster / thumbnail image URL</Label>
            <Input
              value={draft.poster_url || ''}
              onChange={(e) => updateField('poster_url', e.target.value)}
              placeholder="https://..."
              data-testid={`story-poster-${story.slot}`}
            />
            {posterPreview && (
              <img
                src={posterPreview}
                alt="Poster"
                className="mt-2 w-full max-w-[280px] aspect-video object-cover rounded border border-slate-200"
              />
            )}
            <p className="text-[10px] text-slate-400 mt-1">Shown on the landing-page card and as the video poster.</p>
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-5">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="gap-1.5"
          data-testid={`story-save-${story.slot}`}
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save changes
        </Button>
      </div>
    </div>
  );
};

export default function SuccessStoriesAdmin({ adminToken }) {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    api.adminListSuccessStories(adminToken)
      .then((d) => setStories(d.stories || []))
      .catch(() => toast.error('Failed to load Success Stories'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, [adminToken]);

  const handleSaved = (updated) => {
    setStories((arr) => arr.map((s) => (s.slot === updated.slot ? { ...s, ...updated } : s)));
  };

  if (loading) {
    return <div className="text-sm text-slate-500 py-8 text-center" data-testid="admin-stories-loading">Loading…</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-success-stories">
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
        <div className="text-sm font-semibold text-slate-900">5 marketing video slots</div>
        <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">
          Upload a polished MP4 (or paste a YouTube / Vimeo URL) for each slot. The landing page renders 1 featured
          hero + 4 supporting tiles. Slots with no video display a polished &ldquo;Coming Soon&rdquo; ribbon —
          so the layout stays professional while you finish production.
        </div>
      </div>

      <div className="space-y-4">
        {stories.map((s) => (
          <StoryEditor key={s.slot} story={s} token={adminToken} onSaved={handleSaved} />
        ))}
      </div>
    </div>
  );
}
